import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import * as dicomParser from 'dicom-parser';
import { getHeaderTags } from '../../src/components/App/Viewer/getVolumeManager';
import xhrRequest from '../internal/xhrRequest';
import { getTransferSyntaxForContentType } from './loadImage';

import { fromRSArrayBufferResponse } from './getLib3dDataSet';

const { findIndexOfString } = cornerstoneWADOImageLoader.wadors;
// const { xhrRequest } = cornerstoneWADOImageLoader.internal;

const TypeByTransferSyntax = {
  '1.2.840.10008.1.2.4.50': 'image/jpeg',
  '1.2.840.10008.1.2.5': 'image/x-dicom-rle',
  '1.2.840.10008.1.2.4.80': 'image/jls',
  '1.2.840.10008.1.2.4.70': 'image/jll',
  '1.2.840.10008.1.2.4.90': 'image/jp2',
  '1.2.840.10008.1.2.4.92': 'image/jpx',
};

const JPEG_LOSSLESS_TRANSFER_SYNTAX = '1.2.840.10008.1.2.4.57';

function getContentTypeFromHeaderIfValid(xhr) {
  let headerContentType = xhr.getResponseHeader('Content-Type');
  if (headerContentType && headerContentType.includes('transfer-syntax')) {
    let transferSyntax = getTransferSyntaxForContentType(headerContentType);
    if (TypeByTransferSyntax.hasOwnProperty(transferSyntax)) {
      return headerContentType;
    }
  }

  return undefined;
}

function getContentTypeFromPixelData(pixelData) {
  // Some WADO-RS proxies return an octet-stream part without transfer-syntax.
  // JPEG Lossless uses the SOF3 marker and must not be decoded as raw pixels.
  for (let i = 0; i < pixelData.length - 1; i++) {
    if (pixelData[i] === 0xff && pixelData[i + 1] === 0xc3) {
      return `application/octet-stream; transfer-syntax=${JPEG_LOSSLESS_TRANSFER_SYNTAX}`;
    }
  }

  return undefined;
}

function extractEncapsulatedFrame(pixelData) {
  const itemTagLength = 8;
  const hasTag = (offset, element) => pixelData[offset] === 0xfe
    && pixelData[offset + 1] === 0xff
    && pixelData[offset + 2] === element
    && pixelData[offset + 3] === 0xe0;
  const isItemTag = pixelData.length >= itemTagLength && hasTag(0, 0x00);

  if (!isItemTag) {
    return pixelData;
  }

  const fragments = [];
  let offset = 0;
  let itemIndex = 0;

  while (offset + itemTagLength <= pixelData.length) {
    const isItem = hasTag(offset, 0x00);
    const isSequenceDelimiter = hasTag(offset, 0xdd);

    if (isSequenceDelimiter) {
      break;
    }
    if (!isItem) {
      return pixelData;
    }

    const fragmentLength = new DataView(
      pixelData.buffer,
      pixelData.byteOffset + offset + 4,
      4
    ).getUint32(0, true);
    offset += itemTagLength;

    if (fragmentLength < 0 || offset + fragmentLength > pixelData.length) {
      return pixelData;
    }

    // The first item is the Basic Offset Table, not compressed pixel data.
    if (itemIndex > 0 && fragmentLength > 0) {
      fragments.push(pixelData.subarray(offset, offset + fragmentLength));
    }
    offset += fragmentLength;
    itemIndex += 1;
  }

  if (fragments.length === 1) {
    return fragments[0];
  }
  if (fragments.length === 0) {
    return pixelData;
  }

  const frameLength = fragments.reduce((length, fragment) => length + fragment.length, 0);
  const frame = new Uint8Array(frameLength);
  let frameOffset = 0;

  fragments.forEach((fragment) => {
    frame.set(fragment, frameOffset);
    frameOffset += fragment.length;
  });

  return frame;
}

export const findBoundary = (header) => {
  for (let i = 0; i < header.length; i++) {
    if (header[i].substr(0, 2) === '--') {
      return header[i];
    }
  }
};

export const findContentType = (header) => {
  for (let i = 0; i < header.length; i++) {
    if (header[i].substr(0, 13) === 'Content-Type:') {
      return header[i].substr(13).trim();
    }
  }
  return null;
};

export const uint8ArrayToString = (data, offset = 0, length) => {
  const size = length || data.length - offset;
  let str = '';

  for (let i = offset; i < offset + size; i++) {
    str += String.fromCharCode(data[i]);
  }

  return str;
};

const resolveParseDicomImageFrame = (resolve, reject, imageFrameAsArrayBuffer) => {
  const imageFrameAsUint8Array = new Uint8Array(imageFrameAsArrayBuffer);
  const dataSet = dicomParser.parseDicom(imageFrameAsUint8Array);
  const pixelDataElement = dataSet.elements.x7fe00010; // Pixel Data tag

  if (!pixelDataElement) {
    reject(new Error('invalid response - no pixel data found'));
    return;
  }

  const pixelData = new Uint8Array(
    imageFrameAsArrayBuffer,
    pixelDataElement.dataOffset,
    pixelDataElement.length
  );

  const dataSetLib3d = dicomParser.parseDicom(imageFrameAsUint8Array);

  resolve({
    imageFrame: {
      pixelData,
      dataSetLib3d,
    },
  });
};

function getPixelData(requestUri, imageId, mediaType = 'application/octet-stream') {
  const requestHeaders = {
    Accept: mediaType,
  };

  return new Promise((resolve, reject) => {
    const loadPromise = xhrRequest(requestUri, imageId, requestHeaders);

    loadPromise.then((xhr /* , xhr */) => {
      const imageFrameAsArrayBuffer = xhr.response;
      // request succeeded, Parse the multi-part mime response
      const imageFrameUint8Array = new Uint8Array(imageFrameAsArrayBuffer);

      // First look for the multipart mime header
      const tokenIndex = findIndexOfString(imageFrameUint8Array, '\r\n\r\n');

      if (tokenIndex === -1) {
        resolveParseDicomImageFrame(resolve, reject, imageFrameAsArrayBuffer);
        return;
      }

      const headerString = uint8ArrayToString(imageFrameUint8Array, 0, tokenIndex);

      // Now find the boundary marker
      const headers = headerString.split('\r\n');
      const boundary = findBoundary(headers);

      if (!boundary) {
        resolveParseDicomImageFrame(resolve, reject, imageFrameAsArrayBuffer);
        return;
      }
      const offset = tokenIndex + 4; // skip over the \r\n\r\n

      // find the terminal boundary marker
      const endIndex = findIndexOfString(imageFrameUint8Array, boundary, offset);

      if (endIndex === -1) {
        reject(new Error('invalid response - terminating boundary not found'));
        return;
      }

      // Remove \r\n from the length
      const length = endIndex - offset - 2;

      let dataSetLib3d = null;
      let pixelData = new Uint8Array(imageFrameAsArrayBuffer, offset, length);

      let contentType = findContentType(headers).toLowerCase();
      if (contentType.includes('application/dicom')) {
        dataSetLib3d = fromRSArrayBufferResponse(imageFrameAsArrayBuffer);

        let dataSet = dicomParser.parseDicom(pixelData);
        let tags = getHeaderTags(dataSet);

        contentType = TypeByTransferSyntax[tags.transferSyntaxUid];

        const pixelDataElement = dataSet.elements.x7fe00010;

        if (pixelDataElement.encapsulatedPixelData) {
          let bot = pixelDataElement.basicOffsetTable;
          if (bot.length === 0) {
            bot = dicomParser.createJPEGBasicOffsetTable(dataSet, pixelDataElement);
          }

          pixelData = dicomParser.readEncapsulatedImageFrame(dataSet, pixelDataElement, 0, bot);
        } else {
          const dataOffset = pixelDataElement.dataOffset;
          const length1 = pixelDataElement.length;

          pixelData = new Uint8Array(
            dataSet.byteArray.buffer,
            dataSet.byteArray.byteOffset + dataOffset,
            length1
          );
        }
      } else if (contentType.includes('application/octet-stream')) {
        pixelData = extractEncapsulatedFrame(pixelData);
        contentType = getContentTypeFromHeaderIfValid(xhr)
          ?? getContentTypeFromPixelData(pixelData)
          ?? 'application/octet-stream';
      }

      resolve({
        contentType,
        imageFrame: {
          pixelData,
          dataSetLib3d,
        },
      });
    }, reject);
  });
}

export default getPixelData;
